import type { ConditionSchema, z } from '@bunce/schema'
import type { CampaignStepInterface } from '~/types'

export function findStepByIdFrom(
  step: CampaignStepInterface,
  id: number,
): CampaignStepInterface | undefined {
  if (step.id === id) {
    return step
  }

  let allChildren = [] as Array<CampaignStepInterface>

  if (
    (step.type === 'send-broadcast' || step.type === 'delay') &&
    step.value.children.length > 0
  ) {
    allChildren = step.value.children as Array<CampaignStepInterface>
  } else if (
    step.type === 'condition' ||
    step.type === 'transaction_check' ||
    step.type === 'payment_processor_check'
  ) {
    allChildren = step.value.then.concat(
      step.value.else,
    ) as Array<CampaignStepInterface>
  } else if (step.type === 'multi-condition') {
    allChildren = step.value.flatMap((a) => a.then as Array<CampaignStepInterface>)
  }

  for (const child of allChildren) {
    const foundStep = findStepByIdFrom(child, id)
    if (foundStep) {
      return foundStep
    }
  }
  return undefined
}

export function getStepIdAndChildrenIds(step: CampaignStepInterface) {
  const result: Array<string> = [step.id.toString()]
  if (step.type === 'delay' || step.type === 'send-broadcast') {
    result.push(...step.value.children.flatMap((a) => getStepIdAndChildrenIds(a)))
  } else if (
    step.type === 'condition' ||
    step.type === 'transaction_check' ||
    step.type === 'payment_processor_check'
  ) {
    result.push(
      ...step.value.else.flatMap((a) => getStepIdAndChildrenIds(a)),
      ...step.value.then.flatMap((a) => getStepIdAndChildrenIds(a)),
    )
  }

  return result
}

export function findParentsOfStep(
  steps: Array<CampaignStepInterface>,
  targetId: CampaignStepInterface['id'],
) {
  const parents = [] as Array<CampaignStepInterface>

  // Function to recursively find parent steps
  function findParent(
    step: CampaignStepInterface,
    parentStack: Array<CampaignStepInterface>,
  ) {
    // If step has children, check each child recursively
    let allChildren = [] as Array<CampaignStepInterface>

    if (
      (step.type === 'send-broadcast' || step.type === 'delay') &&
      step.value.children.length > 0
    ) {
      allChildren = step.value.children
    } else if (
      step.type === 'condition' ||
      step.type === 'transaction_check' ||
      step.type === 'payment_processor_check'
    ) {
      allChildren = step.value.then.concat(step.value.else)
    } else if (step.type === 'multi-condition') {
      allChildren = step.value.flatMap((a) => a.then)
    }

    // If this step contains the targetId, add the parent to the stack
    for (const child of allChildren) {
      if (child.id === targetId) {
        parents.push(...parentStack, step)
        return // No need to continue checking deeper levels once found
      }
      // Otherwise, recursively check the child
      findParent(child, [...parentStack, step])
    }
  }

  // Iterate over all steps to check for matching parent-child relationship
  for (const step of steps) {
    findParent(step, []) // Start with an empty stack of parents
  }

  return parents
}

export function stepToText(step: CampaignStepInterface): string {
  // todo: "condition" | "transaction_check" | "multi-condition" | "payment_processor_check"
  if (step.type === 'send-broadcast') {
    return `Send ${step.value.broadcast.channel.replace('_', ' ')}: ${step.value.broadcast.subject}`
  } else if (step.type === 'delay') {
    return `Time Delay: ${step.value.value} ${step.value.unit}`
  } else if (step.type === 'condition') {
    const firstCondition = step.value.conditions.at(0)!

    switch (firstCondition?.type) {
      case 'message':
        return firstCondition.property.subject + ' is ' + firstCondition.value
      case 'attribute':
        return `${firstCondition.property.name} ${operatorSymbolToText(firstCondition.operator)} ${firstCondition.value}`
      case 'event':
        return `In ${
          Array.isArray(firstCondition.value)
            ? firstCondition.value.map((a) => a.name).join(', ')
            : firstCondition.value.name
        }`
    }
  }

  return ''
}

export function operatorSymbolToText(operator: string) {
  switch (operator) {
    case 'eq':
      return 'equal'
    case 'lt':
      return 'less than'
    case 'gt':
      return 'greater than'
    case 'not':
      return 'not equal'
    case 'contains':
      return 'contains'
    case 'not_contains':
      return 'does not contain'
    case 'between':
      return 'between'
    case 'previous_days_lte':
      return 'previous day(s)'
    case 'previous_weeks_lte':
      return 'previous week(s)'
    case 'previous_months_lte':
      return 'previous month(s)'
    case 'previous_years_lte':
      return 'previous year(s)'
    case 'not_previous_days_lte':
      return 'not previous day(s)'
    case 'not_previous_weeks_lte':
      return 'not previous week(s)'
    case 'not_previous_months_lte':
      return 'not previous month(s)'
    case 'not_previous_years_lte':
      return 'not previous year(s)'
    case 'offset_days':
      return 'next x days'
    case 'offset_weeks_lte':
      return 'next x weeks'
    case 'offset_months_lte':
      return 'next x months'
    case 'offset_years_lte':
      return 'next x years'
    default:
      return operator
  }
}

function conditionCleaner(conditions: Array<z.infer<typeof ConditionSchema>>) {
  return conditions.map((b) => {
    switch (b.type) {
      case 'event':
        // @ts-expect-error: reducing the payload to just the id(s) and preserving additional parameters
        if (Array.isArray(b.value)) {
          b.additional_parameters = b.value.flatMap((a) =>
            (a.additional_parameters ?? []).map((s: any) => ({
              ...s,
              event_id: a.id,
            })),
          )
          b.value = b.value.map((a) => a.id).join(',')
        } else {
          b.additional_parameters = (b.value.additional_parameters ?? []).map(
            (s: any) => ({
              ...s,
              event_id: b.value.id,
            }),
          )
          b.value = b.value.id
        }
        break
      case 'segment':
        // @ts-expect-error: reducing the payload to just the id (s)
        b.value = Array.isArray(b.value)
          ? b.value.map((a) => a.id).join(',')
          : b.value.id
        break
      case 'attribute':
      case 'message':
        // @ts-expect-error: intentional
        b.property = b.property.id
        break
    }

    return b
  })
}

/**
 * Cleaner function to reduce data sent to the api server
 * @param step
 * @return CampaignStepInterface
 */
function stepCleaner(step: CampaignStepInterface) {
  switch (step.type) {
    case 'send-broadcast':
      // @ts-expect-error: intentional
      step.value.broadcast = step.value.broadcast.id
      step.value.children = (
        step.value.children as Array<CampaignStepInterface>
      ).map(stepCleaner)
      break
    case 'delay':
      step.value.children = (
        step.value.children as Array<CampaignStepInterface>
      ).map(stepCleaner)
      break
    case 'transaction_check':
      step.value.then = (step.value.then as Array<CampaignStepInterface>).map(
        stepCleaner,
      )
      step.value.else = (step.value.else as Array<CampaignStepInterface>).map(
        stepCleaner,
      )
      break
    case 'payment_processor_check':
      step.value.then = (step.value.then as Array<CampaignStepInterface>).map(
        stepCleaner,
      )
      step.value.else = (step.value.else as Array<CampaignStepInterface>).map(
        stepCleaner,
      )
      break
    case 'condition':
      step.value.then = (step.value.then as Array<CampaignStepInterface>).map(
        stepCleaner,
      )
      step.value.else = (step.value.else as Array<CampaignStepInterface>).map(
        stepCleaner,
      )
      step.value.conditions = conditionCleaner(step.value.conditions)
      break
    case 'multi-condition':
      step.value = step.value.map((a) => {
        a.then = (a.then as Array<CampaignStepInterface>).map(stepCleaner)
        a.conditions = conditionCleaner(a.conditions)
        return a
      })
      break
  }

  return step
}

/**
 * Clean automation steps before sending to the api server.
 *
 * @param steps
 */
export function cleanStep(steps: Array<CampaignStepInterface>) {
  return JSON.parse(JSON.stringify(steps)).map(stepCleaner)
}

/**
 * Prepare event conditions for form editing.
 *
 * The API returns `additional_parameters` at the condition level (sibling to `value`),
 * but the Multiselect form expects each event object in `value` to have:
 * - `value` (= id) and `label` (= name) for Multiselect display
 * - `additional_parameters` merged in from the condition level
 *
 * This function walks all steps recursively and transforms event conditions in-place.
 */
function prepareConditionsForForm(conditions: any[]) {
  conditions?.forEach((condition) => {
    if (condition.type === 'event' && Array.isArray(condition.value)) {
      const params: any[] = condition.additional_parameters ?? []
      condition.value = condition.value.map((ev: any) => ({
        ...ev,
        value: ev.id,
        label: ev.name,
        additional_parameters: params.filter((p) => p.event_id === ev.id),
      }))
    }
  })
}

function prepareStepForForm(step: CampaignStepInterface) {
  if (
    step.type === 'condition' ||
    step.type === 'transaction_check' ||
    step.type === 'payment_processor_check'
  ) {
    prepareConditionsForForm(step.value.conditions)
    ;(step.value.then as Array<CampaignStepInterface>).forEach(prepareStepForForm)
    ;(step.value.else as Array<CampaignStepInterface>).forEach(prepareStepForForm)
  } else if (step.type === 'multi-condition') {
    step.value.forEach((path: any) => {
      prepareConditionsForForm(path.conditions)
      ;(path.then as Array<CampaignStepInterface>).forEach(prepareStepForForm)
    })
  } else if (step.type === 'send-broadcast' || step.type === 'delay') {
    ;(step.value.children as Array<CampaignStepInterface>).forEach(
      prepareStepForForm,
    )
  }
}

export function prepareStepsForForm(steps: Array<CampaignStepInterface>) {
  steps.forEach(prepareStepForForm)
  return steps
}
